import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, CheckCircle, Shield, Award, MapPin, Droplets, Flame, Gauge } from 'lucide-react';

const PHONE = "403-615-8770";

const trustChips = [
  { icon: Shield, text: 'Licensed & Insured' },
  { icon: Award, text: 'Journeyman Plumber & Gas Fitter' },
  { icon: MapPin, text: 'Calgary Since 2009' },
  { icon: CheckCircle, text: '25+ Years Experience' },
];

const services = [
  { label: 'Plumbing', path: '/plumbing', icon: Droplets, sub: 'Repairs · Installs · Renovations' },
  { label: 'Heating', path: '/heating', icon: Flame, sub: 'Furnace · Boiler · In-Floor' },
  { label: 'Gas Fitting', path: '/gas-fitting', icon: Gauge, sub: 'Lines · BBQ · Appliances' },
];

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-white"></div>

      {/* One subtle background blob */}
      <div className="blob-droplet w-[420px] h-[420px] -top-40 -left-40 opacity-25"></div>

      {/* Centered hero content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 text-center">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-7"
        >
          <span className="eyebrow">
            Calgary &middot; Owner-Operated Since 2009
          </span>
        </motion.div>

        {/* Big centered headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-heading font-extrabold text-[#0F172A] leading-[1.05] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
        >
          Plumbing.{' '}
          <span className="text-[#0284C7]">Heating.</span>
          <br />
          Gas Fitting.
          <br />
          <span className="block mt-3 text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-slate-700 font-bold">
            Done properly, the first time.
          </span>
        </motion.h1>

        {/* Subhead */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-7 text-slate-600 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
        >
          DNB is owner-operated by <strong className="text-[#0F172A]">Mike Radinovic</strong>, journeyman plumber &amp; gas fitter with over 25 years of hands-on experience. Honest service, clean workmanship, practical solutions &mdash; for your home or business.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center"
        >
          <Link
            to="/contact"
            data-testid="hero-book-btn"
            className="btn-primary inline-flex items-center justify-center gap-2"
          >
            Get a Free Estimate
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href={`tel:${PHONE}`}
            data-testid="hero-call-btn"
            className="btn-secondary inline-flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Call {PHONE}
          </a>
        </motion.div>

        {/* Trust chips inline row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-12 flex flex-wrap justify-center gap-2.5"
        >
          {trustChips.map((c) => (
            <div key={c.text} className="flex items-center gap-2 px-3.5 py-2 bg-white rounded-full border border-slate-200">
              <c.icon className="w-4 h-4 text-[#0284C7]" />
              <span className="text-xs font-semibold text-slate-700">{c.text}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Service strip — clean white card with subtle border */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 md:p-4 bg-white rounded-lg shadow-xl shadow-slate-200/60 border border-slate-200"
        >
          {services.map((s) => (
            <Link
              key={s.label}
              to={s.path}
              data-testid={`hero-service-${s.label.toLowerCase().replace(/ /g, '-')}`}
              className="group flex items-center gap-4 p-5 rounded-lg hover:bg-slate-50 transition-all border border-transparent hover:border-slate-200"
            >
              <div className="droplet-icon droplet-icon-sm flex-shrink-0">
                <s.icon className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-[#0F172A] text-lg leading-tight group-hover:text-[#0284C7] transition-colors">
                  {s.label}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#0284C7] group-hover:translate-x-1 transition-all flex-shrink-0" />
            </Link>
          ))}
        </motion.div>
      </div>

      {/* Bottom separator */}
      <div className="h-12 bg-white"></div>
    </section>
  );
};

export default Hero;
